const DEFAULT_INACTIVITY_REMINDER_MS = 3 * 60 * 1000;
const DEFAULT_SESSION_TIMEOUT_MS = 5 * 60 * 1000;

const parsePositiveNumber = (value: string | undefined): number | null => {
    if (value == null || value.trim() === '') {
        return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Resolves a duration from optional minute/ms env overrides, falling back to a default.
 * Minutes take precedence when both are provided.
 */
export const resolveDurationMs = (
    minutesEnv: string | undefined,
    msEnv: string | undefined,
    fallbackMs: number
): number => {
    const minutes = parsePositiveNumber(minutesEnv);
    if (minutes != null) {
        return minutes * 60 * 1000;
    }

    const ms = parsePositiveNumber(msEnv);
    if (ms != null) {
        return ms;
    }

    return fallbackMs;
};

export const INACTIVITY_REMINDER_MS = resolveDurationMs(
    process.env.REACT_APP_INACTIVITY_REMINDER_MINUTES,
    process.env.REACT_APP_INACTIVITY_REMINDER_MS,
    DEFAULT_INACTIVITY_REMINDER_MS
);

export const SESSION_TIMEOUT_MS = resolveDurationMs(
    process.env.REACT_APP_SESSION_TIMEOUT_MINUTES,
    process.env.REACT_APP_SESSION_TIMEOUT_MS,
    DEFAULT_SESSION_TIMEOUT_MS
);

const formatDurationLabel = (ms: number): string => {
    const totalSeconds = Math.max(1, Math.round(ms / 1000));
    if (totalSeconds < 60) {
        return totalSeconds === 1 ? '1 second' : `${totalSeconds} seconds`;
    }

    const minutes = Math.round(totalSeconds / 60);
    return minutes === 1 ? '1 minute' : `${minutes} minutes`;
};

export const getInactivityReminderText = (
    reminderMs: number = INACTIVITY_REMINDER_MS,
    timeoutMs: number = SESSION_TIMEOUT_MS
): string => {
    const remainingMs = Math.max(timeoutMs - reminderMs, 0);
    return `Are you still there? Your chat session will expire in ${formatDurationLabel(remainingMs)} due to inactivity.`;
};

export const SESSION_EXPIRED_RESTART_TEXT =
    'Your previous chat session has expired due to inactivity. A new session has now started.';

export const INACTIVITY_CONTINUE_ACTION = 'local_continue_chat';
export const INACTIVITY_CANCEL_ACTION = 'local_cancel_chat';

export const INACTIVITY_REMINDER_ID_PREFIX = 'inactive-';
export const SESSION_EXPIRED_NOTICE_ID_PREFIX = 'session-expired-';
export const CHAT_SESSION_EXPIRED_STORAGE_KEY = 'chatSessionExpired';

export const markChatSessionExpired = (): void => {
    sessionStorage.setItem(CHAT_SESSION_EXPIRED_STORAGE_KEY, 'true');
};

export const consumeChatSessionExpiredNotice = (): boolean => {
    if (sessionStorage.getItem(CHAT_SESSION_EXPIRED_STORAGE_KEY) !== 'true') {
        return false;
    }

    sessionStorage.removeItem(CHAT_SESSION_EXPIRED_STORAGE_KEY);
    return true;
};
